<script>
	import logbookService from "../service.js";
	import { onMount, onDestroy } from "svelte";
	import { format as t } from "svelte-i18n"; // Usamos 't' para brevedad en el template
	import { notificate } from "../../../../../services/notification.js";
	import { params } from "@roxi/routify";

	let id = $params.id;

	// Estados reactivos
	let logbook = $state({});
	let entries = $state({ data: [] });
	let loading = $state(true);
	let error = $state(null);

	// Estados para errores de validación de formulario
	let formError = $state(null);
	let editFormError = $state(null);

	const now = new Date();
	const year = now.getFullYear();
	// Sumamos 1 al mes porque getMonth() empieza en 0
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	const hours = String(now.getHours()).padStart(2, "0");
	const minutes = String(now.getMinutes()).padStart(2, "0");
	let entryDatetime = $state(`${year}-${month}-${day}T${hours}:${minutes}`);
	let draftDateTime = $state(null);
	let entryObservations = $state("");
	let entryFiles = $state([]);

	// Control de dropdowns
	let actionsOpen = $state(false);
	let entryMenuOpenId = $state(null);

	// Estado para edición
	let editingEntryId = $state(null);
	let editDraft = $state({ summary: null, date_time: null, attachments: [] });
	let editFiles = $state([]);

	function toggleActionsMenu() {
		actionsOpen = !actionsOpen;
	}

	function toggleEntryMenu(entryId) {
		entryMenuOpenId = entryMenuOpenId === entryId ? null : entryId;
	}

	function closeAllMenus() {
		actionsOpen = false;
		entryMenuOpenId = null;
	}

	function onGlobalPointerDown(e) {
		if (!e.target.closest?.(".actions-dropdown")) closeAllMenus();
	}

	function onGlobalKeyDown(e) {
		if (e.key === "Escape") closeAllMenus();
	}

	function getExecutorLabel(key) {
		return key.split("@")[0];
	}

	const getData = async () => {
		loading = true;
		try {
			let pLogbook = await logbookService.find(id, {});
			logbook = { ...pLogbook };
			let pEntries = await logbookService.searchEntries(id, {});
			entries.data = pEntries.sort(
				(a, b) => new Date(b.date_time) - new Date(a.date_time),
			);
		} catch (e) {
			error = $t("logbook.error_loading");
		} finally {
			loading = false;
		}
	};

	async function toggleLogbookStatus() {
		if (logbook.status == "open") {
			let response = await logbookService.close(logbook.id, {});
			if (response.status == "closed") {
				logbook.status = "closed";
			}
		} else {
			let response = await logbookService.open(logbook.id, {});
			if (response.status == "open") {
				logbook.status = "open";
			}
		}
	}

	async function handleAddEntry() {
		formError = null;

		if (!entryObservations.trim()) {
			formError = $t("logbook.new_entry.error_missing_obs");
			return;
		}

		const payload = {
			logbook_entry_summary: entryObservations,
			logbook_entry_datetime: new Date(entryDatetime).toISOString(),
		};

		try {
			let savedEntry = await logbookService.saveEntry(id, payload, []);

			if (entryFiles.length > 0) {
				try {
					await logbookService.saveAttachment(
						id,
						savedEntry.id,
						entryFiles,
					);
				} catch (e) {
					console.error("Error subiendo archivos:", e);
					formError = $t("logbook.new_entry.error_files_failed");
				}
			}

			const updatedEntry = await logbookService.findEntry(
				id,
				savedEntry.id,
			);

			entries.data = [
				updatedEntry,
				...entries.data.filter((e) => e.id !== updatedEntry.id),
			];

			entryObservations = "";
			entryFiles = [];
			const fileInput = document.querySelector('input[type="file"]');
			if (fileInput) fileInput.value = "";
		} catch (e) {
			console.error("Error:", e);
			formError = $t("logbook.new_entry.error_save");
		}
	}

	function formatToLocalDateTime(dateValue) {
		if (!dateValue) return ""; // Por si viene nulo o vacío

		const d = new Date(dateValue);
		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");
		const hours = String(d.getHours()).padStart(2, "0");
		const minutes = String(d.getMinutes()).padStart(2, "0");

		return `${year}-${month}-${day}T${hours}:${minutes}`;
	}

	function startEditEntry(entry) {
		closeAllMenus();
		editFormError = null;
		editingEntryId = entry.id;
		editFiles = [];
		draftDateTime = formatToLocalDateTime(entry.date_time);
		editDraft.date_time = draftDateTime;
		editDraft.attachments = entry.attachments;
		editDraft.summary = entry.summary;
	}

	function cancelEditEntry() {
		editingEntryId = null;
		editDraft.date_time = null;
		editDraft.attachments = [];
		editDraft.summary = null;
		editFiles = [];
		editFormError = null;
	}

	async function handleEditEntry() {
		editFormError = null;

		try {
			if (editFiles.length > 0) {
				try {
					await logbookService.saveAttachment(
						id,
						editingEntryId,
						editFiles,
					);
				} catch (e) {
					console.error("Error subiendo archivos:", e);
					editFormError = $t("logbook.new_entry.error_files_failed");
				}
			}
			const response = await logbookService.editEntry(
				id,
				editingEntryId,
				{
					summary: editDraft.summary,
					date_time: editDraft.date_time,
				},
			);

			const updatedEntry = response;

			const index = entries.data.findIndex(
				(e) => e.id === editingEntryId,
			);
			if (index !== -1) {
				entries.data[index] = updatedEntry;
			}
			closeAllMenus();
			cancelEditEntry();
		} catch (e) {
			console.error(e);
			editFormError = $t("logbook.edit_form.error_save");
		}
	}

	async function handleDeleteEntry(entryId) {
		await logbookService.deleteEntry(id, entryId, {});
		entries.data = entries.data.filter((entry) => entry.id != entryId);
		closeAllMenus();
	}

	function handleFileChange(e, type) {
		const selected = Array.from(e.target.files);
		if (selected.length > 10) {
			const msg = $t("logbook.new_entry.error_max_files");
			if (type === "new") formError = msg;
			else editFormError = msg;
			e.target.value = "";
			return;
		}
		if (type === "new") {
			entryFiles = selected;
			formError = null;
		} else {
			editFiles = selected;
			editFormError = null;
		}
	}

	let isLoading = $state(false);
	let errorMessage = $state("");

	const handleAttachmentDownload = async (
		entry_id,
		attachment_id,
		file_name,
	) => {
		isLoading = true;
		errorMessage = "";

		try {
			const response = await logbookService.downloadAttachment(
				id,
				entry_id,
				attachment_id,
			);
			if (!response || !response.ok) throw new Error();

			const fileBlob = await response.blob();
			const fileUrl = URL.createObjectURL(fileBlob);

			const anchor = document.createElement("a");
			anchor.href = fileUrl;
			anchor.download = file_name;
			document.body.appendChild(anchor);
			anchor.click();

			document.body.removeChild(anchor);
			URL.revokeObjectURL(fileUrl);
		} catch (error) {
			errorMessage = $t("logbook.edit_form.error_download");
		} finally {
			isLoading = false;
		}
	};

	const handleDeleteAttachment = async (entry_id, att_id) => {
		await logbookService.deleteAttachment(id, entry_id, att_id);
		const updatedEntry = await logbookService.findEntry(id, entry_id);

		entries.data = [
			updatedEntry,
			...entries.data.filter((e) => e.id !== updatedEntry.id),
		];

	};

	onMount(async () => {
		document.addEventListener("pointerdown", onGlobalPointerDown, true);
		document.addEventListener("keydown", onGlobalKeyDown, true);
		await getData();
	});

	onDestroy(() => {
		document.removeEventListener("pointerdown", onGlobalPointerDown, true);
		document.removeEventListener("keydown", onGlobalKeyDown, true);
	});
</script>

{#if loading}
	<div class="container-fluid py-4 text-muted">{$t("logbook.loading")}</div>
{:else if error}
	<div class="container-fluid py-4">
		<div class="alert alert-danger">{error}</div>
	</div>
{:else}
	<div class="container-fluid py-4">
		<!-- HEADER -->
		<div class="mb-4">
			<div
				class="d-flex align-items-center gap-3 mb-3 justify-content-between"
			>
				<h2 class="h4 mb-0 fw-bold text-dark">
					{logbook.name ?? $t("logbook.timeline.no_summary")}
				</h2>
				<span
					class="badge rounded-pill {logbook.status === 'open'
						? 'bg-success-subtle text-success border border-success-subtle'
						: 'bg-secondary-subtle text-secondary border border-secondary-subtle'}"
				>
					{logbook.status === "open"
						? $t("logbook.status_open")
						: $t("logbook.status_closed")}
				</span>
			</div>
			<div
				class="d-flex align-items-center w-100 justify-content-between flex-wrap"
				style="gap: 15px;"
			>
				<a
					href="/vulnerability/custom/logbook"
					class="btn btn-outline-secondary btn-sm"
					>← {$t("logbook.back_to_list")}</a
				>

				<div class="actions-dropdown">
					<button
						class="btn btn-outline-secondary btn-sm"
						onclick={toggleActionsMenu}
						>{$t("logbook.actions")} ▾</button
					>
					{#if actionsOpen}
						<div class="actions-menu">
							<button
								class="actions-item"
								onclick={() => toggleLogbookStatus()}
							>
								{logbook.status === "open"
									? $t("logbook.close_logbook")
									: $t("logbook.reopen_logbook")}
							</button>
						</div>
					{/if}
				</div>
			</div>
		</div>

		<!-- NUEVA ENTRADA -->
		<div class="card mb-4">
			<div class="card-body">
				<div class="d-flex flex-column gap-4">
					<h5 class="card-title mb-0 fw-bold">
						{$t("logbook.new_entry.title")}
					</h5>

					<div class="mt-3">
						<label
							class="form-label fw-semibold small text-muted mb-2"
							>{$t("logbook.new_entry.date_label")}</label
						>
						<div style="max-width: 250px;">
							<input
								type="datetime-local"
								class="form-control"
								bind:value={entryDatetime}
							/>
						</div>
					</div>

					<div class="mt-3">
						<label
							class="form-label fw-semibold small text-muted mb-2"
							>{$t("logbook.new_entry.observations_label")}</label
						>
						<textarea
							class="form-control"
							rows="4"
							bind:value={entryObservations}
							placeholder={$t("logbook.new_entry.placeholder")}
						></textarea>
					</div>

					<div class="mt-3">
						<label
							class="form-label fw-semibold small text-muted mb-2"
							>{$t("logbook.new_entry.attachments_label")}</label
						>
						<input
							type="file"
							class="form-control"
							multiple
							onchange={(e) => handleFileChange(e, "new")}
						/>
					</div>

					<div>
						{#if formError}
							<div class="text-danger small mb-2 fw-bold">
								{formError}
							</div>
						{/if}
						<button
							class="btn btn-primary px-4 mt-3"
							onclick={handleAddEntry}
						>
							{$t("logbook.new_entry.submit")}
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- TIMELINE -->
		<div class="card mb-5">
			<div class="card-body">
				<div
					class="d-flex justify-content-between align-items-center mb-3"
				>
					<h5 class="card-title mb-0 fw-bold">
						{$t("logbook.timeline.title")}
					</h5>
					<span class="badge bg-secondary rounded-pill"
						>{$t("logbook.timeline.entries_count", {
							values: { count: entries.data.length },
						})}</span
					>
				</div>

				{#if entries.data.length === 0}
					<p class="text-muted small text-center py-4">
						{$t("logbook.timeline.empty")}
					</p>
				{:else}
					<div class="list-group list-group-flush">
						{#each entries.data as e (e.id)}
							<div class="list-group-item px-0 py-4">
								<div
									class="d-flex justify-content-between align-items-start"
								>
									<div style="flex: 1; min-width: 0;">
										{#if editingEntryId !== e.id}
											<div
												class=" fw-bold text-muted mb-1"
												style="font-size: 0.65rem;"
											>
												{$t(
													"logbook.timeline.executor",
												)}
											</div>
											<div class="small fw-semibold mb-3">
												{getExecutorLabel(e.created_by)}
											</div>

											<div class="row mb-3">
												<div class="col-md-4">
													<div
														class=" fw-bold text-muted mb-1"
														style="font-size: 0.65rem;"
													>
														{$t(
															"logbook.timeline.date",
														)}
													</div>
													<div
														class="small text-dark"
													>
														{e.date_time}
													</div>
												</div>
											</div>
											<div
												class=" fw-bold text-muted mb-1"
												style="font-size: 0.65rem;"
											>
												{$t("logbook.timeline.summary")}
											</div>
											<div
												class="small text-body mb-2"
												style="white-space: pre-wrap;"
											>
												{e.summary ||
													$t(
														"logbook.timeline.no_summary",
													)}
											</div>

											{#if e.attachments && e.attachments.length > 0}
												<div
													class=" fw-bold text-muted mb-1 mt-3"
													style="font-size: 0.65rem;"
												>
													{$t(
														"logbook.timeline.attachments",
													)}
												</div>
												<div class="mt-2">
													{#each e.attachments as att}
														<a
															href="#"
															class="attachment-badge me-3 d-block mt-2"
															style="cursor: pointer; width: fit-content;"
															role="button"
															tabindex="0"
															onclick={() =>
																handleAttachmentDownload(
																	e.id,
																	att.id,
																	att.file_name,
																)}
															>{att.file_name ||
																att.name}</a
														>
													{/each}
												</div>
											{/if}
										{:else}
											<div
												class="bg-light p-3 rounded mt-2 border"
											>
												<div class="mb-2">
													<label
														class="small fw-bold text-muted"
														>{$t(
															"logbook.edit_form.title_date",
														)}</label
													>
													<input
														type="datetime-local"
														class="form-control form-control-sm"
														bind:value={
															draftDateTime
														}
													/>
												</div>
												<div class="mb-2">
													<label
														class="small fw-bold text-muted"
														>{$t(
															"logbook.edit_form.title_summary",
														)}</label
													>
													<textarea
														class="form-control form-control-sm"
														bind:value={
															editDraft.summary
														}
													></textarea>
												</div>
												{#if e.attachments && e.attachments.length > 0}
													<label
														class="small fw-bold text-muted"
													>
														{$t(
															"logbook.timeline.attachments",
														)}
													</label>
													<div class="mt-2">
														{#each e.attachments as att}
															<div class="d-flex">
																<div
																	href="#"
																	class="attachment-badge me-3 d-block mt-2"
																	style="cursor: pointer; width: fit-content; margin: 0 !important; height: 27px; border-right-width: 0 !important;"
																	role="button"
																	tabindex="0"
																>
																	{att.file_name ||
																		att.name}
																</div>
																<button
																	title={$t(
																		"buttons.delete",
																	)}
																	aria-label={$t(
																		"buttons.delete",
																	)}
																	class="btn btn-secondary table-button btn-delete btn-xs"
																	style="margin: 0 !important; border-radius: 4px !important; background: #e9ecef; border-left-width: 0 !important; position: relative; left: -3px;"
																	onclick={() =>
																		handleDeleteAttachment(
																			e.id,
																			att.id,
																		)}
																>
																	<i
																		class="fas fa-trash"
																	></i>
																</button>
															</div>
														{/each}
													</div>
												{/if}
												{#if e.attachments && e.attachments.length < 10}
													<div class="mb-2">
														<label
															class="small fw-bold text-muted mt-3"
															>{$t(
																"logbook.edit_form.title_attachments",
															)}</label
														>
														<input
															type="file"
															class="form-control form-control-sm"
															multiple
															onchange={(e) =>
																handleFileChange(
																	e,
																	"edit",
																)}
														/>
													</div>
												{/if}

												{#if editFormError}
													<div
														class="text-danger small mb-3 fw-bold"
													>
														{editFormError}
													</div>
												{/if}

												<div class="d-flex gap-2 mt-3">
													<button
														class="btn btn-primary btn-sm me-3"
														onclick={handleEditEntry}
														>{$t(
															"logbook.edit_form.save",
														)}</button
													>
													<button
														class="btn btn-secondary btn-sm"
														onclick={cancelEditEntry}
														>{$t(
															"logbook.edit_form.cancel",
														)}</button
													>
												</div>
											</div>
										{/if}
									</div>

									<div class="actions-dropdown ms-3">
										{#if editingEntryId !== e.id}
											<button
												class="btn btn-outline-secondary btn-sm"
												onclick={() =>
													toggleEntryMenu(e.id)}
											>
												{$t("logbook.actions")} ▾
											</button>
											{#if entryMenuOpenId === e.id}
												<div class="actions-menu">
													<button
														class="actions-item"
														onclick={() =>
															startEditEntry(e)}
													>
														{$t(
															"logbook.timeline.edit_entry",
														)}
													</button>
													<button
														class="actions-item danger deleteEntryBtn"
														disabled={e.attachments
															.length > 0}
														onclick={() =>
															handleDeleteEntry(
																e.id,
															)}
													>
														{$t(
															"logbook.timeline.delete_entry",
														)}
													</button>
												</div>
											{/if}
										{/if}
									</div>
								</div>
							</div>
						{/each}
					</div>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	/* Estilos se mantienen iguales */
	.actions-dropdown {
		position: relative;
	}
	.actions-menu {
		position: absolute;
		top: 100%;
		right: 0;
		min-width: 180px;
		background: #fff;
		border: 1px solid #dee2e6;
		border-radius: 8px;
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
		z-index: 1050;
		padding: 6px;
		margin-top: 4px;
	}
	.actions-item {
		width: 100%;
		border: 0;
		background: none;
		text-align: left;
		padding: 8px 12px;
		font-size: 0.85rem;
		border-radius: 6px;
		color: #333;
		display: block;
	}
	.actions-item:hover {
		background: #f8f9fa;
	}
	.actions-item.danger {
		color: #dc3545;
		font-weight: 500;
	}
	.attachment-badge {
		font-size: 0.75rem;
		background: #e9ecef;
		padding: 2px 8px;
		border-radius: 4px;
		border: 1px solid #dee2e6;
	}
	.deleteEntryBtn:disabled {
		opacity: 0.7;
	}
</style>
